import React, { useState, useEffect} from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Button,
  IconButton,
  Typography,
  styled,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";

// Types
import { SeriesSerializer, SeriesType } from "./types/MeGenTypes";

export type ModCreateSeriesScriptProps = {
  open: boolean;
  projectId: number;
  editMode?: boolean;
  viewMode?: boolean;
  editSeries?: SeriesSerializer;
  onClose: () => void;
  onSave: (data: SeriesSerializer) => void;
};

type ScriptFormState = {
  id?: number;
  name: string;
  series_type: SeriesType;
};

// Styled Components
const StyledDialog = styled(Dialog)({
  "& .MuiDialog-paper": {
    borderRadius: "0.75rem",
    minWidth: "450px",
    maxWidth: "500px",
  },
});

const StyledDialogTitle = styled(DialogTitle)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "1rem 1.5rem",
});

const TitleText = styled(Typography)({
  fontSize: "1.125rem",
  fontWeight: 600,
  color: "#1e293b",
});

const StyledDialogContent = styled(DialogContent)({
  padding: "1.5rem",
});

const StyledDialogActions = styled(DialogActions)({
  padding: "1rem 1.5rem",
  borderTop: "1px solid #e2e8f0",
  gap: "0.75rem",
});

const FormSection = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "1.25rem",
});

const FormLabel = styled(Typography)({
  fontSize: "0.875rem",
  fontWeight: 600,
  color: "#1e293b",
  marginBottom: "0.5rem",
});

const StyledTextField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "white",
    borderRadius: "0.5rem",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": { borderColor: "#e91e63", borderWidth: "1px" },
  },
  "& .MuiInputBase-input": {
    padding: "12px 14px",
    fontSize: "0.875rem",
    color: "#334155",
  },
});

const NotesList = styled(Box)({
  marginTop: "0.5rem",
});

const NoteItem = styled(Typography)({
  fontSize: "0.75rem",
  color: "#64748b",
  marginBottom: "0.25rem",
  display: "flex",
  alignItems: "flex-start",
  gap: "0.5rem",
  "&::before": { content: '"•"', color: "#94a3b8" },
});

const CancelButton = styled(Button)({
  color: "#64748b",
  textTransform: "none",
  fontWeight: 500,
  fontSize: "0.875rem",
  padding: "8px 16px",
  borderRadius: "0.5rem",
  "&:hover": { backgroundColor: "#f1f5f9" },
});

const SaveButton = styled(Button)({
  backgroundColor: "#f382a0",
  color: "white",
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.875rem",
  padding: "8px 20px",
  borderRadius: "0.5rem",
  boxShadow: "0 2px 8px rgba(243, 130, 160, 0.3)",
  "&:hover": {
    backgroundColor: "#e91e63",
    boxShadow: "0 4px 12px rgba(243, 130, 160, 0.4)",
  },
  "&:disabled": {
    backgroundColor: "#cbd5e1",
    color: "#94a3b8",
  },
});

const CloseButton = styled(Button)({
  backgroundColor: "#f1f5f9",
  color: "#64748b",
  textTransform: "none",
  fontWeight: 500,
  fontSize: "0.875rem",
  padding: "8px 20px",
  borderRadius: "0.5rem",
  "&:hover": {
    backgroundColor: "#e2e8f0",
  },
});

// Helper function สำหรับสร้าง initial state
const getInitialFormState = (
  editMode: boolean,
  viewMode: boolean,
  editSeries: SeriesSerializer | undefined
): ScriptFormState => {
  if ((editMode || viewMode) && editSeries) {
    return {
      id: editSeries.id,
      name: editSeries.name,
      series_type: editSeries.series_type,
    };
  }

  return {
    id: undefined,
    name: "",
    series_type: "story2scene",
  };
};

const ModCreateSeriesScript: React.FC<ModCreateSeriesScriptProps> = (props) => {
  const editMode = props.editMode ?? false;
  const viewMode = props.viewMode ?? false;

  const [formState, setFormState] = useState<ScriptFormState>(() =>
    getInitialFormState(editMode, viewMode, props.editSeries)
  );

  // Reset form เฉพาะเมื่อ modal เปิด
  useEffect(() => {
    if (props.open) {
      setFormState(getInitialFormState(editMode, viewMode, props.editSeries));
    }
  }, [props.open]);

  // Handlers
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormState((prev) => ({ ...prev, name: value }));
  };

  const handleSave = () => {
    const payload: SeriesSerializer = {
      project_id: props.projectId,
      name: formState.name,
      series_type: "story2scene",
      schedule_config: {},
    };

    if (editMode && formState.id) {
      payload.id = formState.id;
    }

    props.onSave(payload);
  };

  const getTitle = () => {
    if (viewMode) return "Playlist Details (Script)";
    if (editMode) return "Edit Playlist (Script)";
    return "Create Playlist (Script)";
  };

  const isFormValid = formState.name.trim().length > 0;

  return (
    <StyledDialog id="megen-mod-series-script-container" open={props.open} onClose={props.onClose} maxWidth="sm" fullWidth>
      <StyledDialogTitle id="megen-mod-series-script-header">
        <TitleText id="megen-mod-series-script-title">
          {getTitle()}
        </TitleText>
        <IconButton id="megen-mod-series-script-close-btn" size="small" onClick={props.onClose} sx={{ color: "#94a3b8" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </StyledDialogTitle>

      <StyledDialogContent id="megen-mod-series-script-content">
        <FormSection>
          {/* Playlist Name */}
          <Box>
            <FormLabel>Playlist Name</FormLabel>
            <StyledTextField
              id="megen-mod-series-script-name-input"
              fullWidth
              placeholder="Name of Playlist"
              value={formState.name}
              onChange={handleNameChange}
              autoFocus={!viewMode}
              disabled={viewMode}
              InputProps={{
                readOnly: viewMode
              }}
            />
          </Box>

          {/* Notes */}
          {!viewMode && (
            <NotesList>
              <NoteItem>สร้าง Playlist สำหรับเขียน Script เอง</NoteItem>
              <NoteItem>คุณสามารถเขียน Script และกำหนดรายละเอียดได้ในหน้าถัดไป</NoteItem>
            </NotesList>
          )}
        </FormSection>
      </StyledDialogContent>

      <StyledDialogActions id="megen-mod-series-script-footer">
        {viewMode ? (
          <CloseButton id="megen-mod-series-script-close-view-btn" onClick={props.onClose}>Close</CloseButton>
        ) : (
          <>
            <CancelButton id="megen-mod-series-script-cancel-btn" onClick={props.onClose}>Cancel</CancelButton>
            <SaveButton
              id="megen-mod-series-script-submit-btn"
              startIcon={<SaveIcon fontSize="small" />}
              onClick={handleSave}
              disabled={!isFormValid}
            >
              {editMode ? "Update" : "Create"}
            </SaveButton>
          </>
        )}
      </StyledDialogActions>
    </StyledDialog>
  );
};

export default ModCreateSeriesScript;